import { ReactNode } from 'react';
import { Form, Input, Select, Switch, Divider } from 'antd';
import { useWatch } from 'antd/es/form/Form';

import { FormInstance } from 'antd/lib';

import styles from './index.module.scss';
import InputFile from '../InputFile';
import { Seo } from '../../../types/seo';

const { TextArea } = Input;

interface Props <T extends {seo: Seo, slug?: string}>{
  initialValues?: T;
  form: FormInstance;
  onChange?: () => void;
  /** Path prefix that is fixed in the public URL (e.g. "states"). */
  slugPrefix?: string;
  /** Example of the changeable slug segment (e.g. "alabama"). */
  slugExample?: string;
}

const defaultSlugTooltip = (
  <div>
    <p>
      The URL / slug is the part of the URL that appears after the domain name.
    </p>
    <p>
      For example, if the URL is https://www.example.com/blog/my-article, the URL / slug is
      &quot;blog/my-article&quot;.
    </p>
  </div>
);

function getPrefixedSlugTooltip(prefix: string, example: string): ReactNode {
  return (
    <div>
      <p>
        Enter only the changeable part of the URL, not the full path.
      </p>
      <p>
        {`For example, if the public URL is https://www.example.com/${
          prefix}/${example}, enter "${example}". Do not enter "${prefix}/${example}".`}
      </p>
    </div>
  );
}

export default function SeoForm<T extends {seo: Seo, slug?: string}>({
  form,
  initialValues,
  onChange,
  slugPrefix,
  slugExample = 'my-slug',
}: Props<T>) {
  const isIndexed = useWatch(['seo', 'isIndexed'], form);
  const image = useWatch(['seo', 'ogImage'], form);

  const handleIsIndexedChange = (checked: boolean) => {
    form.setFieldsValue({
      seo: {
        isIndexed: checked,
      },
    });
    onChange?.();
  };

  return (
    <>
      <Divider orientation="left" className="divider-with-title">
        SEO Settings
      </Divider>

      <div className={styles.content}>
        <div className={styles.fildsWrapper}>
          <Form.Item
            label="URL / Slug"
            // name={['seo', 'urlSlug']}
            name="slug"
            rules={[
              { required: true },
              {
                validator: (_, value?: string) => {
                  if (!value) {
                    return Promise.resolve();
                  }

                  if (value.startsWith('/') || value.endsWith('/')) {
                    return Promise.reject(
                      new Error('Slug cannot start or end with "/"'),
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
            tooltip={
              slugPrefix
                ? getPrefixedSlugTooltip(slugPrefix, slugExample)
                : defaultSlugTooltip
            }
          >
            <Input
              placeholder="Please enter"
              maxLength={150}
              showCount={{
                formatter: ({ count, maxLength }) => `${count}/${maxLength}`,
              }}
            />
          </Form.Item>

          <Form.Item
            label="Meta Title"
            name={['seo', 'metaTitle']}
            rules={[{ required: true, max: 100 }]}
          >
            <Input
              placeholder="Please enter"
              maxLength={100}
              showCount={{
                formatter: ({ count, maxLength }) => `${count}/${maxLength}`,
              }}
            />
          </Form.Item>

          <Form.Item
            label="Keywords"
            name={['seo', 'keywords']}
            rules={[{ required: true }]}
          >
            <Select
              mode="tags"
              placeholder="Please enter"
              tokenSeparators={[',']}
            />
          </Form.Item>

          <Form.Item
            label="Meta Description"
            name={['seo', 'metaDescription']}
            rules={[{ max: 160 }]}
            tooltip={(
              <div>
                <p>
                  If description is not provided, it will be generated automatically from html content.
                </p>
              </div>
            )}
          >
            <TextArea
              placeholder="Please enter"
              maxLength={160}
              showCount={{
                formatter: ({ count, maxLength }) => `${count}/${maxLength}`,
              }}
            />
          </Form.Item>

          <div className={styles.label}>
            Search engine indexing
          </div>

          <Form.Item name={['seo', 'isIndexed']}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Switch
                checked={isIndexed}
                onChange={handleIsIndexedChange}
              />
              Index this topic
            </div>
          </Form.Item>

          <div className={styles.label}>
            Open Graph / Social
          </div>

          <Form.Item
            label="OG title (optional)"
            name={['seo', 'ogTitle']}
            rules={[{ max: 100 }]}
          >
            <Input
              placeholder="Please enter"
              maxLength={100}
              showCount={{
                formatter: ({ count, maxLength }) => `${count}/${maxLength}`,
              }}
            />
          </Form.Item>

          <Form.Item
            label="OG description (optional)"
            name={['seo', 'ogDescription']}
            rules={[{ max: 160 }]}
          >
            <TextArea
              placeholder="Please enter"
              maxLength={160}
              showCount={{
                formatter: ({ count, maxLength }) => `${count}/${maxLength}`,
              }}
            />
          </Form.Item>

          <InputFile
            form={form}
            name={['seo', 'ogImage']}
            label="Image (optional)"
            rules={[{ required: false }]}
            description="Recommended image size: 1200 × 630 px (WebP, PNG, or JPG)"
            aspect={1200 / 630}
            defaultLocation={image}
            valueKey="location"
            uploadAreaClasses={styles.uploadAreaClasses}
            onChange={onChange}
          />
        </div>
      </div>
    </>
  );
}
